import React, { memo, useEffect } from 'react';
import { FormGroup, FormControlLabel, Checkbox, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import usePersistentStore, { useGlobalBucket } from '../../store/persistent/persistentStore';
import { grid } from '../../../Global/global_variables';
import { Panels } from '@gui/constants/Panels';
import type { ContextState } from '@visualizer/types';
import { BuildingSource } from '@visualizer/types';
import { getSentence } from '../../constants/sentences';
import { getGrid } from '../../../store/LoadingStore';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { getViewer } from 'store/viewerStore';

type ContextProps = {
    showContent: boolean;
};

const Context: React.FC<ContextProps> = ({ showContent }) => {
    const geodataItems = ['Ground', 'Buildings', 'Vegetation', 'Roads', 'Targets', 'Wind Stations'] as const;
    const geodataKeyMap = {
        Ground: 'showGround',
        Buildings: 'showBuildings',
        Vegetation: 'showTrees',
        Roads: 'showRoads',
        Targets: 'showTargets',
        'Wind Stations': 'showWindStations',
    } as const;

    const AnalysisGridKeyMap = {
        'Sensor Points': 'showGrid',
        'Site Center': 'showGridCenter',
        'Site Axes': 'showGridAxes',
        'Analysis Volume': 'showAnalysisVolume',
        'Analysis Box': 'showSiteBox',
        'Analysis Border': 'showAnalysisBorder',
        'Legal Border': 'showLegalBorder',
        'Buildable border': 'showBuildableBorder',
    } as const satisfies Record<string, keyof ContextState>;

    type AnalysisGridItem = keyof typeof AnalysisGridKeyMap;

    const AnalysisGridItems: AnalysisGridItem[] = [
        'Sensor Points',
        'Site Center',
        'Site Axes',
        'Analysis Volume',
        'Analysis Box',
        'Analysis Border',
        'Legal Border',
        'Buildable border',
    ];

    const context = useGlobalBucket((s) => s.context);
    const setPanel = usePersistentStore((s) => s.setPanel);

    const setContextPanel = (updates: Partial<ContextState>) => setPanel(updates, Panels.CONTEXT);

    const buildings = getGrid().getActiveGeodata().dataObject.Buildings;
    const hasCustomObstruction = buildings?.hasVisibleBuildings(BuildingSource.CUSTOM_OBS) ?? false;
    const hasProjectSketch = buildings?.hasVisibleBuildings(BuildingSource.SKETCH) ?? false;
    const customObstructionDisabled = !hasCustomObstruction;
    const projectSketchDisabled = !hasProjectSketch;

    useEffect(() => {
        getGrid()
            ?.getActiveGeodata()
            .getIteratorLegacy()
            .filter((geoObj) => geoObj != null)
            .forEach((geoObject) => {
                geoObject.toggleVisibility();
            });
    }, [context.showGround, context.showRoads, context.showTargets]);

    useEffect(() => {
        getGrid()
            ?.getGeodata()
            .getIteratorLegacy()
            .filter((geoObj) => geoObj != null)
            .forEach((geoObject) => {
                geoObject.updateColors();
            });
        getViewer().updateBackground();
    }, [context.showTargets]);

    useEffect(() => {
        getGrid().getActiveGeodata().dataObject.Buildings?.setVisible(context.showBuildings);
    }, [context.showBuildings]);

    useEffect(() => {
        getGrid()
            .getActiveGeodata()
            .dataObject.Buildings?.setBuildingSourceVisible(BuildingSource.CUSTOM_OBS, context.showCustomObstruction);
    }, [context.showCustomObstruction]);

    useEffect(() => {
        getGrid().getActiveGeodata().dataObject.Buildings?.setBuildingSourceVisible(BuildingSource.SKETCH, context.showProjectSketch);
    }, [context.showProjectSketch]);

    useEffect(() => {
        getGrid().getActiveGeodata().dataObject.Trees?.setVisible(context.showTrees);
    }, [context.showTrees]);

    useEffect(() => {
        grid?.contextItems?.toggleVisibility();
    }, [
        context.showLegalBorder,
        context.showBuildableBorder,
        context.showAnalysisBorder,
        context.showGridCenter,
        context.showSiteBox,
        context.showAnalysisVolume,
        context.showGridAxes,
        context.showWindStations,
        context.showRoads,
        context.showTargets,
    ]);

    if (!showContent) return null;

    return (
        <Grid container spacing={2}>
            <Grid size={6}>
                <Typography fontWeight="bold">Geodata</Typography>
                <FormGroup className="panel-compact-control-stack">
                    {geodataItems.map((item) => (
                        <CustomTooltip
                            side="left"
                            key={item}
                            mode="icon"
                            title={item === 'Roads' && getSentence('Context.Checkbox.Roads.Tooltip')}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        name={item}
                                        checked={context[geodataKeyMap[item]]}
                                        onChange={(event) => setContextPanel({ [geodataKeyMap[item]]: event.target.checked })}
                                    />
                                }
                                label={
                                    <Typography
                                        component="span"
                                        sx={{ lineHeight: 1.1, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}
                                    >
                                        {item}
                                    </Typography>
                                }
                            />
                        </CustomTooltip>
                    ))}
                    <FormControlLabel
                        key="Custom Obstruction"
                        disabled={customObstructionDisabled}
                        control={
                            <Checkbox
                                size="small"
                                name="Custom Obstruction"
                                checked={context.showCustomObstruction}
                                disabled={customObstructionDisabled}
                                onChange={(event) => setContextPanel({ showCustomObstruction: event.target.checked })}
                            />
                        }
                        label={
                            <Typography
                                component="span"
                                sx={{
                                    lineHeight: 1.1,
                                    color: customObstructionDisabled ? 'text.disabled' : 'inherit',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                }}
                            >
                                {'Custom Obstr'}
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        key="Project Sketch"
                        disabled={projectSketchDisabled}
                        control={
                            <Checkbox
                                size="small"
                                name="Project Sketch"
                                checked={context['showProjectSketch']}
                                disabled={projectSketchDisabled}
                                onChange={(event) => setContextPanel({ showProjectSketch: event.target.checked })}
                            />
                        }
                        label={
                            <Typography
                                component="span"
                                sx={{
                                    lineHeight: 1.1,
                                    color: projectSketchDisabled ? 'text.disabled' : 'inherit',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                }}
                            >
                                {'Project Sketch'}
                            </Typography>
                        }
                    />
                </FormGroup>
            </Grid>
            <Grid size={6}>
                <Typography fontWeight="bold">Grid</Typography>
                <FormGroup className="panel-compact-control-stack">
                    {AnalysisGridItems.map((item) => {
                        return (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        name={item}
                                        checked={context[AnalysisGridKeyMap[item]]}
                                        onChange={(event) =>
                                            setContextPanel({
                                                [AnalysisGridKeyMap[item]]: event.target.checked,
                                            })
                                        }
                                    />
                                }
                                label={
                                    <Typography
                                        component="span"
                                        sx={{
                                            minWidth: 90,
                                            lineHeight: 1.1,
                                            whiteSpace: 'nowrap',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {item}
                                    </Typography>
                                }
                            />
                        );
                    })}
                </FormGroup>
            </Grid>
        </Grid>
    );
};

export default memo(Context);
